/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  useEffect, useState, useMemo, ChangeEvent, memo,
} from 'react';

import classNames from 'classnames';
import { Data } from '@styled-icons/boxicons-regular';
import {
  findNode, findNodePath, getNode, getNodeParent, getRange, insertText, select, TNode,
} from '@udecode/plate-core';
import { Location, Path, Range } from 'slate';
import { cloneDeep, isEqual } from 'lodash';
import { Link, RefreshCcw } from '@styled-icons/feather';
import { DreamBlock, IConnectionDataKey } from '@typedream/core';
import styled from 'styled-components';
import sharedStyles from './Shared.module.css';
import styles from '../../SideToolbar.module.css';
import { getTPlugin, useTPlateEditorRef } from '../../../../plate';
import { ELEMENT_IMAGE, ELEMENT_LINK, KEY_COLLECTION_CONNECT_DATA } from '../../../../pluginKeys';
import { BLOCK_ELEMENT_KEY } from '../../../../nodes/collection/components/CollectionDataElement/types';
import {
  ALLOWED_CONNECT_TYPES,
  CONNECTED_TYPES_OPTIONS_IMAGE,
  CONNECTED_TYPES_OPTIONS_TEXTS,
  IAllowedConnectedTypes,
  LEVEL, SPECIAL_SCHEMA_COLUMN_NAME, TEXT_TYPES,
  COLLECTION_CONNECT_ITEM_PAGE_URL,
} from '../../../../constants';
import { CollectionDataPluginOptions, IOptionsElement } from '../../../../nodes/collection/types';
import LoadingSmall from './LoadingSmall';
import CheckboxItem from './CheckboxItem';
import OptionsContainer from './Options';
import { Dropdown, DropdownItem } from '../../../../components/Dropdown';
import { removeLinkAtSelection, upsertLinkAtSelection } from '../../../../nodes/link/transforms';
import { removeElementLink, setBlockProperty } from '../../../../editorTools/transforms';
import {
  changeLinkOpenInNewTab, getText, isLinkableElement,
} from '../../../../editorTools/queries';
import { useDreamEditorStore } from '../../../../plugins/store';
import { addConnectDataAtSelection, removeConnectDataAtSelection } from '../../../../nodes/collection/components/CollectionDataElement/transforms';

const dummyText = 'Lorem ipsum dolor sit amet';

const DivToolbarSection = styled.div`
  margin: 12px 18px;
  background-color: transparent;
  color: var(--text-light);
  font-size: 13px;
`;

const DivLabel = styled.div`
  color: var(--text-lighter);
  margin-bottom: 4px;
  display: block;
`;

function ConnectData() {
  const editor = useTPlateEditorRef();
  const { options: opt } = getTPlugin<
  CollectionDataPluginOptions>(editor, KEY_COLLECTION_CONNECT_DATA);

  const {
    columns,
    listColumns,
    listColumnsLoading,
    updateSchema,
    schema,
  } = opt as CollectionDataPluginOptions;
  const [currentIsOpenInNewTab, setCurrentIsOpenInNewTab] = useState(false);
  const [inputIsOpenInNewTab, setInputIsOpenInNewTab] = useState(false);

  const [fieldsOpen, setfieldsOpen] = useState({});
  const [newSchema, setnewSchema] = useState({});
  const [currentConnectedData, setcurrentConnectedData] = useState({});
  const [inputConnectedData, setinputConnectedData] = useState(currentConnectedData);
  const element = useDreamEditorStore((state) => {
    const path = state.selectedElementPaths[0];
    if (!path) return null;
    try {
      return getNode<DreamBlock>(state.editor as TNode, path);
    } catch {
      return null;
    }
  }) as DreamBlock & { open_in_new_tab:boolean };
  const selection = useDreamEditorStore(
    (state) => state.editor?.selection,
    (a, b) => isEqual(a, b),
  );
  const path = findNodePath(editor, element) as Path;
  const generatedColumns: Array<string> = useMemo(() => (Object.keys(columns || {}).filter((col) => col.startsWith('TD:'))), [columns]);
  const otherColumns: Array<string> = useMemo(() => (Object.keys(columns || {}).filter((col) => !col.startsWith('TD:'))), [columns]);

  const urlBlockElementKey = element?.type === ELEMENT_IMAGE
    ? BLOCK_ELEMENT_KEY.HREF : BLOCK_ELEMENT_KEY.URL;

  const connectedTypes = Object.values((ALLOWED_CONNECT_TYPES as any)[element?.type] || {});

  const onChange = (e: ChangeEvent<HTMLInputElement>, elementKey: string | number, level: any) => {
    const originalSchemaKey = (inputConnectedData as any)?.[elementKey]?.schemaKey;
    const newSchemaKey = e.target.value;
    const newInputConnectedData = {
      ...inputConnectedData,
      [elementKey]: {
        schemaKey: newSchemaKey,
        blockElementKey: elementKey,
        level,
      },
    };
    const originalColumn = (newSchema as any)[originalSchemaKey];

    const updatedNewSchema = {
      ...newSchema,
      [originalSchemaKey]: undefined,
      [newSchemaKey]: originalColumn,
    };
    if (Object.prototype.hasOwnProperty.call(updatedNewSchema, originalSchemaKey)) {
      delete (updatedNewSchema as any)[originalSchemaKey];
    }
    setinputConnectedData(newInputConnectedData);
    setnewSchema(updatedNewSchema);
  };

  const toggleConnectData = (elementKey: string, level: LEVEL) => {
    const newSelected = !(inputConnectedData as any)?.[elementKey];
    let newInputConnectedData = cloneDeep(inputConnectedData);
    let updatedNewSchema = newSchema;

    if (!newSelected) {
      const originalSchemaKey = (inputConnectedData as any)?.[elementKey]?.schemaKey;
      newInputConnectedData = {
        ...inputConnectedData,
        [elementKey]: undefined,
      };
      if (Object.prototype.hasOwnProperty.call(newInputConnectedData, elementKey)) {
        delete (newInputConnectedData as any)[elementKey];
      }
      updatedNewSchema = {
        ...newSchema,
        [originalSchemaKey]: undefined,
      };
      if (Object.prototype.hasOwnProperty.call(updatedNewSchema, originalSchemaKey)) {
        delete (updatedNewSchema as any)[originalSchemaKey];
      }
    } else {
      let autofill = 'Untitled';
      const randomSuffix = Math.random()
        .toString(36)
        .substring(5, 5 + 2);
      if (element?.type === ELEMENT_IMAGE) {
        autofill = `${(CONNECTED_TYPES_OPTIONS_IMAGE as any)[elementKey]?.autofill}-${element?.id.slice(0, 2)}${randomSuffix}`;
      } else {
        autofill = `${(CONNECTED_TYPES_OPTIONS_TEXTS as any)[elementKey]?.autofill}-${element?.id?.slice(0, 2)}${randomSuffix}`;
      }
      const newSchemaKey = autofill;
      newInputConnectedData = {
        ...inputConnectedData,
        [elementKey]: {
          schemaKey: autofill,
          blockElementKey: elementKey,
          level,
        },
      };
      updatedNewSchema = {
        ...newSchema,
        [newSchemaKey]: null,
      };
    }

    setinputConnectedData(newInputConnectedData);
    setnewSchema(updatedNewSchema);
  };

  const checkCanConnectLeaf = (_element: DreamBlock) => Object.values(
    (ALLOWED_CONNECT_TYPES as any)?.[_element?.type] || {},
  )?.filter(
    (allowedKeys) => (allowedKeys as unknown as IAllowedConnectedTypes).level === LEVEL.LEAF,
  )?.length > 0;

  const onSave = () => {
    if (!element && !editor.selection) return;
    let leafDataKeysURL = null;
    const leafDataKeys = Object.values(inputConnectedData).filter(
      (v) => (v as unknown as IConnectionDataKey & { level: LEVEL }).level === LEVEL.LEAF,
    ).map((el) => {
      const v = el as unknown as IConnectionDataKey;
      if (v.blockElementKey === urlBlockElementKey) {
        leafDataKeysURL = {
          schemaKey: v.schemaKey,
          blockElementKey: v.blockElementKey,
        };
      }

      if (v.blockElementKey !== urlBlockElementKey) {
        return ({
          schemaKey: v.schemaKey,
          blockElementKey: v.blockElementKey,
        });
      }
      return undefined;
    }).filter((v) => !!v);
    const blockDataKeys = Object.values(inputConnectedData).filter(
      (v) => (v as unknown as IConnectionDataKey & { level: LEVEL }).level === LEVEL.BLOCK,
    ).map((v) => ({
      schemaKey: (v as IConnectionDataKey).schemaKey,
      blockElementKey: (v as IConnectionDataKey).blockElementKey,
    }));

    /** Add dummy text / image placeholder if it connects to empty content */
    if (
      TEXT_TYPES.includes(element.type)
      && !getText(editor, { fullBlockText: true })
      && !getText(editor)
    ) {
      insertText(editor, dummyText, { at: path });
    }
    if (element.type === ELEMENT_IMAGE && !element.url) {
      setBlockProperty(editor, BLOCK_ELEMENT_KEY.URL, '/images/image-placeholder-for-connect-data.png', { at: path });
      setBlockProperty(editor, BLOCK_ELEMENT_KEY.ASPECT_RATIO, 1.25, { at: path });
      setBlockProperty(editor, BLOCK_ELEMENT_KEY.WIDTH, 500, { at: path });
      setBlockProperty(editor, BLOCK_ELEMENT_KEY.HEIGHT, 400, { at: path });
    }

    // connect or remove data keys for block level
    setBlockProperty(
      editor,
      BLOCK_ELEMENT_KEY.COLLECTION_CONNECT_DATA,
      !!blockDataKeys,
      {
        at: path,
      },
    );
    if (blockDataKeys) {
      setBlockProperty(
        editor,
        BLOCK_ELEMENT_KEY.COLLECTION_CONNECT_DATA_KEYS,
        blockDataKeys,
        {
          at: path,
        },
      );
    }

    const canConnectLeaf = checkCanConnectLeaf(element);
    if (canConnectLeaf) {
      if (!leafDataKeysURL) {
        const nodeEntry = findNode(editor, {
          at: path,
          match: (n) => n.type === ELEMENT_LINK,
        });

        const linkNode = nodeEntry?.[0];
        const linkPath = nodeEntry?.[1];

        // If previously this is a URL with connected data,
        // remove connected data and remove the link as well.
        // (otherwise, if this is a normal link not connected to data, do nothing)
        if (linkPath && linkNode?.[BLOCK_ELEMENT_KEY.COLLECTION_CONNECT_DATA]) {
          setBlockProperty(
            editor,
            BLOCK_ELEMENT_KEY.COLLECTION_CONNECT_DATA,
            false,
            {
              at: linkPath,
              match: (n) => n.type === ELEMENT_LINK,
            },
          );
          setBlockProperty(
            editor,
            BLOCK_ELEMENT_KEY.COLLECTION_CONNECT_DATA_KEYS,
            [],
            {
              at: linkPath,
              match: (n) => n.type === ELEMENT_LINK,
            },
          );

          if (isLinkableElement(element)) {
            removeElementLink(editor, { at: path });
          } else {
            removeLinkAtSelection(editor);
          }
        }
      }

      // connect or remove data keys for leaf level
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const hasConnectedDataAtPath = (path: Path) => !!getNode(
        editor,
        path,
      )?.[BLOCK_ELEMENT_KEY.COLLECTION_CONNECT_DATA];

      if (
        editor.selection
          && (Range.isCollapsed(editor.selection)
            || hasConnectedDataAtPath(editor.selection.anchor.path))
      ) {
        select(editor, getRange(editor, editor.selection.anchor.path));
      } else {
        select(editor, getRange(editor, editor?.selection as Location));
      }

      if (leafDataKeysURL) {
        // if we're adding connection to URL, need to insertLink to make it a URL
        upsertLinkAtSelection(editor, { url: COLLECTION_CONNECT_ITEM_PAGE_URL });

        const parent = Path.parent(editor.selection?.anchor?.path as number[]);

        setBlockProperty(
          editor,
          BLOCK_ELEMENT_KEY.COLLECTION_CONNECT_DATA,
          true,
          {
            at: parent,
          },
        );
        setBlockProperty(
          editor,
          BLOCK_ELEMENT_KEY.COLLECTION_CONNECT_DATA_KEYS,
          [leafDataKeysURL],
          {
            at: parent,
          },
        );
      }
      if ((leafDataKeys && leafDataKeys?.length > 0) || leafDataKeysURL) {
        addConnectDataAtSelection(editor, leafDataKeys as IConnectionDataKey[]);
      } else {
        removeConnectDataAtSelection(editor, { at: editor.selection });
      }
    }
    updateSchema?.(newSchema);
  };

  const onKeyDown = (e: { key: string; preventDefault: () => void; }) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    onSave();
  };

  useEffect(() => {
    listColumns?.();
  }, [element, path]);

  useEffect(() => {
    const connData = {} as any;
    let openInNewTab = false;
    const canConnectLeaf = checkCanConnectLeaf(element);
    openInNewTab = element?.[BLOCK_ELEMENT_KEY.OPEN_IN_NEW_TAB];

    if (selection && canConnectLeaf) {
      try {
        const el = getNode<IOptionsElement>(editor, selection.focus.path);

        el?.[BLOCK_ELEMENT_KEY.COLLECTION_CONNECT_DATA_KEYS]?.forEach(
          (i) => {
            connData[i.blockElementKey] = {
              schemaKey: i.schemaKey,
              blockElementKey: i.blockElementKey,
              level: (ALLOWED_CONNECT_TYPES as any)?.[element?.type]?.[i.blockElementKey]?.level,
            };
          },
        );
      } catch (e) {
        // do nothing
      }

      try {
        const elParent = getNodeParent<IOptionsElement>(editor, selection.focus.path);
        if (elParent?.type === ELEMENT_LINK) {
          elParent?.[BLOCK_ELEMENT_KEY.COLLECTION_CONNECT_DATA_KEYS]?.forEach(
            (i) => {
              connData[i.blockElementKey] = {
                schemaKey: i.schemaKey,
                blockElementKey: i.blockElementKey,
                level: (ALLOWED_CONNECT_TYPES as any)?.[element?.type]?.[i.blockElementKey]?.level,
              };
            },
          );
          openInNewTab = elParent?.[BLOCK_ELEMENT_KEY.OPEN_IN_NEW_TAB] as boolean;
        }
      } catch (e) {
        // do nothing
      }
    }
    element?.[BLOCK_ELEMENT_KEY.COLLECTION_CONNECT_DATA_KEYS]?.forEach(
      (i) => {
        connData[i.blockElementKey] = {
          schemaKey: i.schemaKey,
          blockElementKey: i.blockElementKey,
          level: (ALLOWED_CONNECT_TYPES as any)?.[element?.type]?.[i.blockElementKey]?.level,
        };
      },
    );

    if (!isEqual(currentConnectedData, connData)) {
      setcurrentConnectedData(connData);
    }
    setCurrentIsOpenInNewTab(openInNewTab);
  }, [editor, element, path,
    columns,
    selection, currentConnectedData]);

  useEffect(() => {
    setinputConnectedData(currentConnectedData);
  }, [currentConnectedData, element]);

  useEffect(() => {
    setInputIsOpenInNewTab(currentIsOpenInNewTab);
  }, [currentIsOpenInNewTab, element]);

  useEffect(() => {
    setnewSchema(schema ?? {});
  }, [schema, element]);

  const isBtnSaveDisabled = isEqual(newSchema, schema)
    && isEqual(currentConnectedData, inputConnectedData)
    && isEqual(currentIsOpenInNewTab, inputIsOpenInNewTab);

  if (!element
  || !columns
  || Object.keys(columns).length === 0
  ) return <div />;

  return (
    <DivToolbarSection>
      <DivLabel>
        Data
      </DivLabel>
      <div>
        <div className={classNames('mb-3', styles.small)}>
          Get text, url, or image from a data source.
        </div>
        {
            connectedTypes?.map((el) => {
              const {
                elementKey, level, defaultSelected, optionDisabled, defaultColumn,
              } = el as any;
              const options = element?.type === ELEMENT_IMAGE
                ? CONNECTED_TYPES_OPTIONS_IMAGE : CONNECTED_TYPES_OPTIONS_TEXTS;
              const schemaKey = (inputConnectedData as any)?.[elementKey]?.schemaKey;
              const schemaColumn = (columns
                && (newSchema as any)?.[schemaKey]
                && Object.keys(columns)?.includes((newSchema as any)[schemaKey]))
                || (SPECIAL_SCHEMA_COLUMN_NAME as any)?.[(newSchema as any)[schemaKey]]
                ? (newSchema as any)[schemaKey] : null;
              return (
                <div className="mb-2 has-fullwidth">
                  <CheckboxItem
                    className={classNames('is-flex is-align-items-center', styles.cursorPointer)}
                    title={(
                      <span className={styles.cursorPointer}>
                        {`Get ${(options as any)[elementKey]?.title} from`}
                        <Data size={14} className="with-accent-text-color mr-1" />
                        <span className="with-accent-text-color">Data</span>
                      </span>
                    )}
                    selected={(inputConnectedData as any)?.[elementKey] || defaultSelected}
                    onClick={optionDisabled
                      ? () => { /** do nothing */ } : () => toggleConnectData(elementKey, level)}
                  />
                  {defaultColumn && (
                    <div className="has-fullwidth mt-2">
                      <div className={classNames(styles.label, styles.small)}>
                        Content
                      </div>
                      <div className="has-fullwidth mb-1">
                        <input
                          className={classNames(styles.menuItem, 'px-3 py-2 has-fullwidth')}
                          type="text"
                          value={defaultColumn}
                          disabled
                        />
                      </div>
                      <div className={styles.small}>
                        You can not change this column.
                      </div>

                      <div>
                        <OptionsContainer
                          selected={false}
                          className={classNames('m-0 has-text-left p-2', styles.inputField, styles.inputDropdown)}
                        >
                          <div className="with-accent-text-color has-text-align-left">{defaultColumn}</div>
                        </OptionsContainer>
                      </div>
                    </div>
                  )}
                  {!defaultColumn && (inputConnectedData as any)?.[elementKey] && (
                    <div className="px-2 mb-1">
                      <div className="has-fullwidth mt-2">
                        <div className={classNames(styles.label, styles.small)}>
                          Label
                        </div>
                        <input
                          className={classNames(sharedStyles.inputOptionsContainer, styles.inputField, 'p-2')}
                          type="text"
                          value={schemaKey}
                          placeholder={`e.g. ${(options as any)[elementKey]?.autofill}`}
                          onChange={(e) => onChange(e, elementKey, level)}
                          onKeyDown={onKeyDown}
                        />
                      </div>
                      <div className="has-fullwidth mt-2">
                        <div className={classNames(styles.label, styles.small)}>
                          Content
                        </div>
                        <div className="is-flex is-align-items-center ">
                          <Dropdown triggerLabel={
                            schemaColumn ? `${(SPECIAL_SCHEMA_COLUMN_NAME as any)?.[schemaColumn] || schemaColumn}` : 'Select Column'
                          }
                          >
                            {elementKey === urlBlockElementKey
                            && (
                            <>
                              <div className="is-size-7 px-1">Internal Link (Recommended)</div>
                              <DropdownItem>
                                <div
                                  className="with-accent-text-color"
                                  role="none"
                                  onClick={() => {
                                    setnewSchema({
                                      ...newSchema,
                                      [schemaKey]: COLLECTION_CONNECT_ITEM_PAGE_URL,
                                    });
                                    setfieldsOpen({
                                      ...fieldsOpen,
                                      [elementKey]: false,
                                    });
                                  }}
                                >
                                  <Link size={13} className="mr-1" />
                                  {(SPECIAL_SCHEMA_COLUMN_NAME as any)?.[
                                    COLLECTION_CONNECT_ITEM_PAGE_URL]}
                                </div>
                              </DropdownItem>
                              <hr className={classNames(styles.hr, 'my-1')} />
                            </>
                            )}
                            {otherColumns.length === 0 && generatedColumns.length === 0
                              ? (
                                <div>
                                  <div className="is-size-7 px-1">
                                    You have not shared any tables with Typedream
                                  </div>
                                </div>
                              )
                              : (
                                <>
                                  <div className={classNames(styles.subLabel, 'px-2')}>All Columns</div>
                                  {otherColumns.map((c) => (

                                    <DropdownItem>
                                      <div
                                        role="none"
                                        onClick={() => {
                                          setnewSchema({
                                            ...newSchema,
                                            [schemaKey]: c,
                                          });
                                          setfieldsOpen({
                                            ...fieldsOpen,
                                            [elementKey]: false,
                                          });
                                        }}
                                      >
                                        {columns[c]?.name}
                                      </div>
                                    </DropdownItem>
                                  ))}
                                  <div className={classNames(styles.subLabel, 'px-2')}>Auto-generated Columns</div>
                                  {generatedColumns.map((c) => (
                                    <DropdownItem>
                                      <div
                                        role="none"
                                        onClick={() => {
                                          setnewSchema({
                                            ...newSchema,
                                            [schemaKey]: c,
                                          });
                                          setfieldsOpen({
                                            ...fieldsOpen,
                                            [elementKey]: false,
                                          });
                                        }}
                                      >
                                        {columns[c]?.name}
                                      </div>
                                    </DropdownItem>
                                  ))}
                                </>
                              )}
                          </Dropdown>
                          {listColumnsLoading ? (
                            <LoadingSmall className="ml-3" loading={listColumnsLoading} />
                          ) : (
                            <div
                              className="with-accent-text-color with-cursor-pointer"
                              role="none"
                              onClick={listColumns}
                            >
                              <RefreshCcw size={16} className="ml-3" />
                            </div>
                          )}
                        </div>
                      </div>
                      {elementKey === urlBlockElementKey
                      && (
                        <div className="is-flex is-align-items-center mt-1">
                          <div className={classNames(styles.small, styles.flex1)}>
                            Open in new tab
                          </div>
                          <div className={sharedStyles.switchContainer}>
                            <input
                              id={`data-url-${element.id}`}
                              key={`data-url-${element.id}`}
                              type="checkbox"
                              name="data-url"
                              className="switch is-rounded is-small is-primary"
                              checked={inputIsOpenInNewTab}
                              onChange={() => {
                                changeLinkOpenInNewTab(editor);
                                setInputIsOpenInNewTab((prev) => !prev);
                              }}
                              disabled={!element}
                            />
                            <label htmlFor={`data-url-${element.id}`} style={{ verticalAlign: 'middle', paddingLeft: 36 }}>
                              {/* no text label */}
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                </div>
              );
            })
          }
        <div className="is-flex is-justify-content-flex-end has-fullwidth">
          <button
            onClick={isBtnSaveDisabled ? () => { /** do nothing */ } : onSave}
            className={classNames(styles.button, styles.buttonSmall, {
              [styles.buttonDisabled]: isBtnSaveDisabled,
            })}
            type="button"
          >
            Save
          </button>
        </div>
      </div>
    </DivToolbarSection>
  );
}

export default memo(ConnectData);
